import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { authService, LoginCredentials, ApiError } from '@/services/auth';
import { useAccountStore } from '@/store/accountStore';

const LoginPage: React.FC = () => {
  // console.log('LoginPage component is rendering!');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts, setActiveAccount } = useAccountStore();
  const isAddingAccount = location.state?.addAccount;
  const currentUser = authService.getCurrentUser();
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountsDropdown, setShowAccountsDropdown] = useState(false);

  // Check if user is already authenticated
  useEffect(() => {
    if (authService.isAuthenticated() && !isAddingAccount) {
      navigate('/inbox', { replace: true });
    }
  }, [navigate, isAddingAccount]);

  const handleInputChange = (field: keyof LoginCredentials, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if this account is already added
      const existingAccount = accounts.find(acc => acc.username === formData.email);
      if (existingAccount) {
        setError('This account is already added. Please use a different account.');
        return;
      }

      const response = await authService.login(formData, isAddingAccount);
      setSuccess(isAddingAccount 
        ? `Account ${response.user.first_name} added successfully!`
        : `Welcome back, ${response.user.first_name}!`
      );
      
      setTimeout(() => {
        if (isAddingAccount) {
          // Force reload to ensure clean state with new account
          window.location.href = '/inbox';
        } else {
          navigate('/inbox', { replace: true });
        }
      }, 1500);
      
    } catch (err) {
      const apiError = err as ApiError;
      
      if (apiError.status === 401) {
        setError('Invalid username or password');
      } else if (apiError.status === 0) {
        setError('Unable to connect to server. Please check your connection.');
      } else {
        setError(apiError.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  try {
    return (
      <div style={{ 
        minHeight: '100vh', 
        width: '100%',
        background: 'linear-gradient(135deg, #EBF4FF 0%, #FFFFFF 50%, #EEF2FF 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundImage: 'url(/backgrounds/bg12.png)',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'repeat'
      }}>
        <div style={{ 
          width: '100%', 
          maxWidth: '400px',
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f3f4f6',
          position: 'relative'
        }}>
          {/* Logo and Welcome Section */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            {isAddingAccount ? (
              <>
                {/* Account Switcher */}
                <div style={{ marginBottom: '24px', position: 'relative' }}>
                  {/* Current Account */}
                  <button
                    onClick={() => setShowAccountsDropdown(!showAccountsDropdown)}
                    style={{ 
                      width: '100%',
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '12px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#6366f1',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0] || ''}
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#111827',
                        marginBottom: '2px'
                      }}>
                        {currentUser?.first_name} {currentUser?.last_name}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280' 
                      }}>
                        {currentUser?.username}
                      </div>
                    </div>
                    <ChevronDown 
                      size={20} 
                      style={{ 
                        color: '#6b7280',
                        transform: showAccountsDropdown ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease'
                      }} 
                    />
                  </button>

                  {/* Accounts Dropdown */}
                  {showAccountsDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 50,
                      padding: '8px'
                    }}>
                      <button
                        onClick={() => {
                          navigate('/inbox');
                          setShowAccountsDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: '#4f46e5',
                          fontSize: '13px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f3ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Return to inbox
                      </button>

                      {accounts.filter(acc => acc.id !== currentUser?.id).map(account => (
                        <button
                          key={account.id}
                          onClick={() => {
                            setActiveAccount(account.id);
                            window.location.href = '/inbox';
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '8px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#e2e8f0',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {account.first_name?.[0]}{account.last_name?.[0] || ''}
                          </div>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#111827'
                            }}>
                              {account.first_name} {account.last_name}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#6b7280' 
                            }}>
                              {account.username}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <img 
                src="/logos/logo.png" 
                alt="QLoop Logo" 
                style={{ 
                  height: '50px', 
                  width: 'auto', 
                  marginBottom: '1rem',
                  marginLeft: 'auto',
                  marginRight: 'auto', 
                  objectFit: 'contain' 
                }} 
              />
            )}
            <h1 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '0.5rem' 
            }}>
              {isAddingAccount ? 'Add another account' : 'Welcome Back'}
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              {isAddingAccount 
                ? 'Sign in to add another Qloop Email account'
                : 'Sign in to your Qloop Email'
              }
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.375rem',
              color: '#dc2626',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '0.375rem',
              color: '#16a34a',
              fontSize: '0.875rem'
            }}>
              {success}
            </div>
          )}
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Email
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  opacity: loading ? 0.6 : 1
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    paddingRight: '2.5rem', // Make room for the toggle button
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    opacity: loading ? 0.6 : 1
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '0.25rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                background: loading ? '#9ca3af' : '#8D00E4',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          
          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '0.5rem' 
            }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                © {new Date().getFullYear()} Qloop. All rights reserved.
              </p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                color: '#4f46e5',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Powered by 
                <a 
                  href="https://theqloop.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    color: '#4f46e5',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  <img 
                    src="/logos/logo-qloop.png" 
                    alt="QLoop" 
                    style={{ 
                      height: '20px', 
                      width: 'auto',
                      objectFit: 'contain'
                    }} 
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  } catch (error) {
    console.error('Error rendering LoginPage:', error);
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'red',
        color: 'white',
        padding: '2rem'
      }}>
        <div>
          <h1>Error rendering LoginPage</h1>
          <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }
};

export default LoginPage; 